import {getTestProps} from '@benchling/js-test-utils';
import {Menu as MantineMenu} from '@mantine/core';
import React, {useMemo} from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import * as ArchiveActions from 'archive/ArchiveActions';
import isArchived from 'archive/isArchived';
import {
  ArchivableToolbarAction,
  buildArchiveToggleAction,
  hideActionsIllegalWhenArchived,
} from 'archive/toolbar/archiveToolbarActions';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Container, ParentStorableData, StorableSummary} from 'constants/SampleConstants';
import * as SampleConstants from 'constants/SampleConstants';
import * as context from 'Context';
import CICODropdown from 'inventory/check-in-check-out/CICODropdown';
import PrintLabelButton from 'label-printing/PrintLabelButton';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import DeleteContainerContentsModal from 'samples/containers/DeleteContainerContentsModal';
import StorableExportButton, {getStorableExportOptions} from 'samples/storables/StorableExportButton';
import TransferModal from 'samples/transfers/TransferModal';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import ActionToolbar from 'ui-library/toolbar/ActionToolbar';
import {ToolbarAction} from 'ui-library/toolbar/ToolbarAction';
import {ElementMouseEvent} from 'util/Types';
import AddToWorklistButton from 'worklists/AddToWorklistButton';

import * as SampleUtil from './SampleUtil';
import * as StorableActions from './StorableActions';
import ChangeParentStorableButton from './storables/ChangeParentStorableButton';

const TOOLBAR_BUTTON_STYLE = ButtonStyle.TERTIARY_GRAY;
const ACTION_ORIGIN = ActionOrigin.INVENTORY_CONTAINER_PAGE_TOOLBAR;

interface Props {
  container: Container;
  onRequestTransfer?: (e: ElementMouseEvent) => void;
  parentStorable?: StorableSummary | null;
  isUnarchivable?: boolean;
  reload?: () => void;
}

interface ToolbarActionsParams {
  container: Container;
  onRequestTransfer?: (e: ElementMouseEvent) => void;
  parentStorable?: StorableSummary | null;
  reload?: () => void;
  /** Whether Archive and the export options live in the overflow menu rather than inline. */
  isMoveToMoreEnabled: boolean;
}

export default function ContainerToolbar({
  container,
  onRequestTransfer,
  parentStorable,
  reload,
}: Props): JSX.Element {
  const {uxMoveArchiveAndUnregisterToMore} = useRolloutFlags();
  const isMoveToMoreEnabled = uxMoveArchiveAndUnregisterToMore === true;
  // Memoized so the action render callbacks keep their identity across unrelated parent re-renders.
  const actions = useMemo(
    () => getToolbarActions({container, onRequestTransfer, parentStorable, reload, isMoveToMoreEnabled}),
    [container, onRequestTransfer, parentStorable, reload, isMoveToMoreEnabled]
  );
  return <ActionToolbar actions={actions} />;
}

/**
 * Routes every control through the shared toolbar: Move leads its own group; Transfer, check-in/out, Print,
 * Worklist, Archive/Unarchive, and Export form the main group. With the move-to-more flag on, Archive and
 * the export options move into the overflow menu instead (Unarchive stays inline). Archived containers are
 * read-only, so the actions that modify the container are hidden for them. The composite controls keep
 * their existing ui-library rendering via each action's `render` callback.
 */
function getToolbarActions({
  container,
  onRequestTransfer,
  parentStorable,
  reload,
  isMoveToMoreEnabled,
}: ToolbarActionsParams): Array<ToolbarAction> {
  const isFixedPlate = SampleUtil.isFixedPlate(parentStorable);
  const actions: Array<ArchivableToolbarAction> = [];

  // Fixed-plate containers cannot be moved, so Move is omitted for them.
  if (!isFixedPlate) {
    actions.push({
      key: 'move',
      placement: 'leading',
      legalWhenArchived: false,
      render: () => (
        <ChangeParentStorableButton
          buttonStyle={TOOLBAR_BUTTON_STYLE}
          isWritable={container.permissions.writable}
          onSetParentStorable={(parentStorableData, eventGroupId, onFinish) => {
            setParentStorable(container.id, parentStorableData, eventGroupId, onFinish);
          }}
          waitForSubmitErrors={true}
          storableSchema={container.storableSchema}
          sampleStatusWarningParams={{
            actionAttempted: 'move_location',
            actionOrigin: ACTION_ORIGIN,
            sampleIds: [container.id],
          }}
        />
      ),
    });
  }
  if (container.permissions.writable) {
    actions.push({
      key: 'transfer',
      placement: 'main',
      legalWhenArchived: false,
      render: () => (
        <ContainerTransferDropdown
          container={container}
          onRequestTransfer={onRequestTransfer}
          reload={reload}
        />
      ),
    });
  }
  actions.push({
    key: 'check-in-check-out',
    placement: 'main',
    legalWhenArchived: false,
    render: () => (
      <CICODropdown actionSource={ACTION_ORIGIN} buttonStyle={TOOLBAR_BUTTON_STYLE} items={[container]} />
    ),
  });
  actions.push({
    key: 'print-label',
    placement: 'main',
    legalWhenArchived: true,
    render: () => <PrintLabelButton buttonStyle={TOOLBAR_BUTTON_STYLE} itemIds={[container.id]} />,
  });
  if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
    actions.push({
      key: 'add-to-worklist',
      placement: 'main',
      legalWhenArchived: true,
      render: () => (
        <AddToWorklistButton
          buttonStyle={TOOLBAR_BUTTON_STYLE}
          ids={[container.id]}
          refreshOptions={{refreshRecentWorklists: true}}
        />
      ),
    });
  }

  // Fixed-plate containers can neither be moved nor archived, so they get no archive toggle.
  const archiveToggle = isFixedPlate
    ? null
    : buildArchiveToggleAction({
        item: container,
        archivePlacement: isMoveToMoreEnabled ? 'overflow' : 'main',
        isArchivable: Boolean(container.permissions.archivable),
        isUnarchivable: Boolean(container.permissions.unarchivable),
        onArchive: (itemIds, purpose, options) => {
          ArchiveActions.bulkArchiveStorablesWithDataMods(itemIds, purpose, options, reload);
        },
        onUnarchive: (ids) => {
          ArchiveActions.bulkUnarchiveStorablesWithDataMods(ids, reload);
        },
        getAnalyticsMetadata: () => ({actionOrigin: ACTION_ORIGIN, eventGroupId: makeAnalyticsEventId()}),
      });
  // Inline, the toggle sits just before Export; in the overflow menu it comes last, below the exports.
  const archiveToggleGoesLast = archiveToggle?.placement === 'overflow';
  if (archiveToggle && !archiveToggleGoesLast) {
    actions.push({...archiveToggle, legalWhenArchived: true});
  }
  actions.push({
    key: 'export',
    placement: isMoveToMoreEnabled ? 'overflow' : 'main',
    legalWhenArchived: true,
    render: isMoveToMoreEnabled
      ? () => <StorableExportMenuItems storableId={container.id} />
      : () => <StorableExportButton storableId={container.id} buttonStyle={TOOLBAR_BUTTON_STYLE} />,
  });
  if (archiveToggle && archiveToggleGoesLast) {
    actions.push({...archiveToggle, legalWhenArchived: true});
  }

  return hideActionsIllegalWhenArchived(actions, isArchived(container));
}

function setParentStorable(
  containerId: string,
  parentStorableData: ParentStorableData,
  eventGroupId: string,
  onFinish?: (errorMessage?: string) => void
): void {
  context.using({analyticsMetadata: {actionOrigin: ACTION_ORIGIN, eventGroupId}}, null, () => {
    StorableActions.updateContainerWithDataMods(containerId, {parentStorableData}, onFinish);
  });
}

function StorableExportMenuItems({storableId}: {storableId: string}): JSX.Element {
  return (
    <>
      {getStorableExportOptions(storableId).map((option) => (
        <MantineMenu.Item
          key={option.key}
          icon={<BenchlingIcon icon="share-v2" />}
          onClick={option.onSelect}
          data-amp-safe={true}
          {...getTestProps({component: 'StorableExport', element: 'menuItem', key: option.key})}
        >
          {option.label}
        </MantineMenu.Item>
      ))}
    </>
  );
}

function ContainerTransferDropdown({
  container,
  onRequestTransfer,
  reload,
}: {
  container: Container;
  onRequestTransfer?: (e: ElementMouseEvent) => void;
  reload?: () => void;
}): JSX.Element {
  const canTransfer = TransferUtil.isSourceContainerValidForTransfer({
    ...container,
    containedItemIds: container.contents.map(({containedItemId}) => containedItemId),
  });
  const isEmpty = container.contents.length === 0;
  return (
    <Dropdown
      tooltip="Transfer in / out"
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
          <Option
            onSelect={() => {
              legacyRunModal(TransferModal, {
                flow: SampleConstants.TransferFlow.TRANSFER_IN,
                actionOrigin: ACTION_ORIGIN,
                title: 'Fill container',
                preselectedOutputConfig: {
                  outputType: ModelTypeConstants.CONTAINER,
                  outputContainerSummaries: [container],
                  outputPlate: null,
                  outputCells: [],
                },
                onSuccess: reload,
              });
            }}
          >
            Fill container
          </Option>
          {onRequestTransfer && (
            <Option
              isDisabled={!canTransfer}
              onSelect={onRequestTransfer}
              tooltipTitle={canTransfer ? undefined : 'Container is empty'}
            >
              Transfer contents
            </Option>
          )}
          <Option
            isDisabled={isEmpty}
            tooltipTitle={isEmpty ? 'Container is empty' : undefined}
            onSelect={() => {
              legacyRunModal(DeleteContainerContentsModal, {
                containerIds: [container.id],
                actionOrigin: ACTION_ORIGIN,
                onSuccess: reload,
              });
            }}
          >
            Remove contents
          </Option>
        </Menu>
      }
    >
      <Button icon="transfer" isDropdown={true} buttonStyle={TOOLBAR_BUTTON_STYLE} size={ButtonSize.SMALL} />
    </Dropdown>
  );
}
